import React, { lazy, Suspense, useEffect } from 'react';
import { ClientProviderWrapper } from '../../modules/commonUtils/ClientProvider/ClientProviderWrapper';

import {
  IntegrationMicroFrontendProps,
  UserDetail
} from './IntegrationMicroFrontend';
import './Integration.scss';
import {
  Card,
  ContentLoader,
  Control,
  Divider,
  Label,
  Select,
  SelectOption,
  SelectOptionSearch,
  Tab,
  Tabs
} from '@forge/common';
import { Loader, Notification, Icon } from '@scuf/common';
import i18next from '../../i18next';
import IntegrationsWorflowMicrofrontendPage from './IntegrationsWorflowMicrofrontendPage';
import EasyIntegrateIframe, {
  EasyIntegrateIframeHandle
} from './EasyIntegrateIframe';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isLegacyWorkflowEnabledForSite } from '../../modules/commonUtils/LDFlagUtils';
import NoSubscription from './NoSubscription';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuthService } from '../../auth/hooks/useAuthService';

const IntegrationMicroFrontend = lazy(() =>
  import('./IntegrationMicroFrontend').then(m => ({
    default: m.IntegrationMicroFrontend
  }))
);

const SELECTED_ORGANIZATION_KEY = 'selected-organization-id' as const;

interface Customer {
  id: string;
  name: string;
}
interface Site {
  id: string;
  siteName: string;
}

const IntegrationMicroFrontendPage: React.FC<IntegrationMicroFrontendProps> = ({
  userDetail
}) => {
  const { authFetch, baseURL } = useAuthService();
  const [customerList, setCustomerList] = React.useState<Array<Customer>>([]);
  const [siteList, setSiteList] = React.useState<Array<Site>>([]);
  const [loadingSites, setLoadingSites] = React.useState<boolean>(false);
  const [selectedSite, setSelectedSite] = React.useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = React.useState<string | null>(
    null
  );
  const [userPrivileges, setUserPrivileges] = React.useState<Array<string>>([]);
  const [hasWorkflowSubscription, setHasWorkflowSubscription] = React.useState<
    boolean | null
  >(null);
  const allowedSubscriptions: string[] =
    (window as any).ENV?.INTWORKFLOW_ALLOWEDSKUS || [];

  const [filteredCustomers, setFilteredCustomers] = React.useState<
    Array<Customer>
  >([]);
  const [showError, setShowError] = React.useState<boolean>(false);
  const [selectedTab, setSelectedTab] = React.useState<string>(
    'api_credentials'
  );
  const [searchString, setSearchString] = React.useState<string>('');
  const [selectedUserDetail, setSelectedUserDetail] = React.useState<
    UserDetail
  >(userDetail as UserDetail);
  const selectedSIOrgId =
    sessionStorage.getItem(SELECTED_ORGANIZATION_KEY) ?? '';
  const [loadingSubscription, setLoadingSubscription] = React.useState<boolean>(
    false
  );
  // True while a Node-RED editor is open full-page; hides the header + tabs.
  const [isEditorFullPage, setIsEditorFullPage] = React.useState<boolean>(
    false
  );
  const history = useHistory();
  const location = useLocation();
  // Imperative handle to the integrations iframe so we can close the editor on browser back.
  const integrationsIframeRef = React.useRef<EasyIntegrateIframeHandle>(null);
  // Latest pathname kept in a ref so the editor handler can stay referentially stable.
  const pathnameRef = React.useRef<string>(location.pathname);
  React.useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);
  // Tracks the editor open/close state we last acted on, so repeated notifications from the
  // iframe (e.g. the editor firing onLoad more than once) don't push the route twice.
  const editorOpenRef = React.useRef<boolean>(false);

  // Fallback tab path used if we somehow can't determine where the editor was opened from.
  const WORKFLOWS_PATH = '/app/feature/integrations/workflows';
  // The tab path the editor was opened from (e.g. /templates or /workflows). The editor can
  // be launched from either the Integrations or the Templates tab, so we capture the
  // originating path on open and return to it on close — otherwise a Back press would always
  // land on the Integrations tab.
  const editorOriginPathRef = React.useRef<string>(WORKFLOWS_PATH);

  // Keep history in sync with the editor open/close state coming from the iframe.
  const handleEditorStateChange = React.useCallback(
    (isOpen: boolean) => {
      // Ignore duplicate notifications for the same state to avoid pushing the route twice.
      if (editorOpenRef.current === isOpen) {
        return;
      }
      editorOpenRef.current = isOpen;
      setIsEditorFullPage(isOpen);
      if (isOpen) {
        if (!pathnameRef.current.endsWith('/editor')) {
          // Remember the originating tab so a Back press / close returns to it (Templates
          // stays on Templates, Integrations stays on Integrations).
          const originPath = pathnameRef.current.replace(/\/+$/, '');
          editorOriginPathRef.current = originPath;
          // Nest the full-page editor route under the originating tab so a browser Back
          // press pops this entry and returns to that exact tab, closing the editor.
          history.push(`${originPath}/editor`);
        }
      } else if (pathnameRef.current.endsWith('/editor')) {
        // Editor closed from within (in-editor back button / close event / org change):
        // replace the /editor entry with the originating tab path so we deterministically
        // land back on the tab the editor was opened from.
        history.replace(editorOriginPathRef.current);
      }
    },
    [history]
  );

  // Browser Back: when we leave the editor route while the editor is still open,
  // tell the iframe to close so the integration tab doesn't render inside the editor space.
 const prevPathnameRef = React.useRef<string>(location.pathname);
  useEffect(() => {
    const prevPath = prevPathnameRef.current;
    const currPath = location.pathname;
    prevPathnameRef.current = currPath;
    const leftEditorRoute =
      prevPath.endsWith('/editor') && !currPath.endsWith('/editor');
    if (leftEditorRoute && isEditorFullPage) {
      integrationsIframeRef.current?.closeEditor();
    }
  }, [location.pathname, isEditorFullPage])

  useEffect(() => {
    (window as any).INTEGRATION_TILE_BASE = (window as any).ENV?.INTEGRATION_TILE_BASE;
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path.endsWith('/editor')) {
      // Full-page editor can be opened from EITHER the Integrations or the Templates tab
      // (both iframes fire the same openNodeRedEditor event). Keep whichever tab is
      // currently selected so the iframe hosting the editor stays mounted; forcing a tab
      // here would unmount the editor we just opened.
      return;
    } else if (path.endsWith('/templates')) {
      setSelectedTab('templates');
    } else if (path.endsWith('/workflows')) {
      setSelectedTab('integrations');
    } else if (path.includes('/workflow')) {
      setSelectedTab('workflows');
    } else if (path.includes('/integration')) {
      setSelectedTab('api_credentials');
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchUserPrivileges = async () => {
      const scopeKey = 'Integration';
      const apiUrl = `${baseURL}/api/v1/users/me/user-all-privileges?scopes=${scopeKey}`;
      const response = await authFetch(apiUrl, { method: 'GET' });
      if (response.status === 200) {
        const privileges = await response.json();
        setUserPrivileges(privileges);
      } else {
        setUserPrivileges([]);
      }
    };

    fetchUserPrivileges();
  }, []);

  const flags = useFlags();
  const { enableWorkFlowsForThirdpartyIntegrations, isNewIntegrationEnabled } = flags;
  const showLegacyWorkflow = isLegacyWorkflowEnabledForSite('legacyWorkflowSiteIds', flags, selectedSite);

  const hasWorkflowPriviliges =
    Array.isArray(userPrivileges) &&
    userPrivileges.find((permission: any) =>
      ['Integration_workflows_read'].includes(permission.name)
    );
  const showWorkflowsTab =
    (!!hasWorkflowPriviliges || selectedTab === 'workflows') && showLegacyWorkflow;

  const getCustomerList = async () => {
    const response = await authFetch(
      `${
        (window as any).ENV.BASE_URL
      }/api/v1/root-nodes/LCBS/${selectedSIOrgId}/accessible-customers`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    if (response.ok) {
      return response.json();
    } else {
      setShowError(true);
    }
  };

  const getSiteList = async () => {
    setSelectedSite(null);
    setLoadingSubscription(true);
    setLoadingSites(true);
    const response = await authFetch(
      `${
        (window as any).ENV.BASE_URL
      }/api/v3/organisations/LCBS/${selectedCustomer}/sites?pageNumber=1&pageSize=100&search=`,
      {
        method: 'GET'
      }
    );
    if (response.ok) {
      return response.json();
    } else {
      setLoadingSubscription(false);
      setLoadingSites(false);
      setShowError(true);
    }
  };

  const getSubscriptionStatus = async () => {
    const response = await authFetch(
      `${
        (window as any).ENV.BASE_URL
      }/api/v4/subscriptions/linkedbundles?orgId=${selectedCustomer}&siteId=${selectedSite}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    if (response.ok) {
      return response.json();
    } else {
      setLoadingSubscription(false);
      setShowError(true);
    }
  };

  const getLcbsSubscriptionDetails = async () => {
    const response = await authFetch(
      `${(window as any).ENV.BASE_URL
      }//api/v1/nodes/lcbs/search/${selectedSite}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    if (response.ok) {
      return response.json();
    } else {
      setLoadingSubscription(false);
      setShowError(true);
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (
      enableWorkFlowsForThirdpartyIntegrations &&
      (selectedTab === 'workflows' || selectedTab === 'integrations' || selectedTab === 'templates') &&
      selectedCustomer &&
      selectedSite
    ) {
      Promise.all([getSubscriptionStatus(), getLcbsSubscriptionDetails()]).then((subsCriptionDetails: any) => {
        if (!isMounted) return;
        const [data, lcbsData] = subsCriptionDetails;
        if (data?.isSuccess) {
          const subscriptions = data.data || [];
          let hasValidSubscription = false;
          hasValidSubscription = subscriptions.some(
            (sub: any) =>
              sub.status === 'Active' &&
              selectedSite === sub.siteId[0] &&
              allowedSubscriptions.includes(sub.skuName)
          );
          // Fallback to check LCBS subscription if no valid subscription found
          const rootNodeMetadata = lcbsData?.metadata || [];
          if (!hasValidSubscription && rootNodeMetadata.length > 0) {
            const rootNodeSubscriptionDetails = rootNodeMetadata.find((meta: any) => meta.name === 'SubscriptionDetails');
            const subscriptionValues = rootNodeSubscriptionDetails ? JSON.parse(rootNodeSubscriptionDetails.value) : [];
            hasValidSubscription = subscriptionValues.some((sub: any) =>
              allowedSubscriptions.includes(sub?.Bundle?.Name)
            );
          }
          setHasWorkflowSubscription(hasValidSubscription);
          setLoadingSubscription(false);
        } else {
          setLoadingSubscription(false);
          setHasWorkflowSubscription(false);
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [
    selectedCustomer,
    selectedSite,
    selectedTab,
    enableWorkFlowsForThirdpartyIntegrations,
    allowedSubscriptions
  ]);

  React.useEffect(() => {
    let isMounted = true;
    if (selectedCustomer) {
      getSiteList().then((data: { sites: Site[] } | any) => {
        setLoadingSites(false);
        if (!isMounted) return;
        if (data?.sites?.length > 0) {
          const sortedSiteList = data.sites.sort((a: Site, b: Site) =>
            a.siteName.localeCompare(b.siteName)
          );
          setSiteList(sortedSiteList);
          setSelectedSite(sortedSiteList[0].id);
          sessionStorage.setItem('fcis_siteId', sortedSiteList[0].id);
        } else {
          setSiteList([]);
          setSelectedSite(null);
          sessionStorage.removeItem('fcis_siteId');
        }
      }).catch(() => {
        setLoadingSites(false);
      });
    } else {
      setSiteList([]);
      setSelectedSite(null);
      sessionStorage.removeItem('fcis_siteId');
    }
    return () => {
      isMounted = false;
    };
  }, [selectedCustomer]);

  React.useEffect(() => {
    if (userDetail?.email) {
      setSelectedUserDetail(userDetail as UserDetail);
    }
  }, [userDetail]);

  React.useEffect(() => {
    getCustomerList().then((data: { resources: Customer[] }) => {
      if (data?.resources?.length > 0) {
        const sortedCustomer = data.resources.sort((a: Customer, b: Customer) =>
          a.name.localeCompare(b.name)
        );
        setCustomerList(sortedCustomer);
        setFilteredCustomers(sortedCustomer);
        setSelectedCustomer(sortedCustomer[0].id);
        sessionStorage.setItem('fcis_projectId', sortedCustomer[0].id);
      } else {
        setCustomerList([]);
        setFilteredCustomers([]);
        setSelectedCustomer(null);
        sessionStorage.removeItem('fcis_projectId');
        sessionStorage.removeItem('fcis_siteId');
      }
    });
  }, [selectedSIOrgId]);

  const handleFilter = (filterValue: string) => {
    setSearchString(filterValue);
    const filterdCustomers = customerList.filter(customer =>
      customer.name
        .toLocaleLowerCase()
        .includes(filterValue.toLocaleLowerCase())
    );
    setFilteredCustomers(filterdCustomers);
  };

  // Org/site context handed to the EasyIntegrate iframe (Integrations & Templates tabs)
  const easyIntegrateOrg = {
    id: selectedCustomer || undefined,
    name:
      customerList.find(customer => customer.id === selectedCustomer)?.name ||
      '',
    siteId: selectedSite || undefined
  };

  return (
    <div
      id="integration-micro-frontend"
      className={`integration-micro-frontend${
        isEditorFullPage ? ' editor-fullpage' : ''
      }`}
    >
      <div className="application-header">
        <div className="header-title">
          <div className="heading-3">{i18next.t('integration')}</div>
          <div>{i18next.t('integrationSubtitle')}</div>
        </div>
        <div className="header-selects">
          <div className="customer-select">
            <Label className="customer-title">{i18next.t('customer')}</Label>
            <Select
              aria-label="select-customer"
              value={selectedCustomer || customerList[0]?.id}
              height={5}
              id="select-customer"
              placeholder={i18next.t('selectCustomer')}
              size="large"
              onChange={e => {
                setSelectedCustomer(e.target.value);
                sessionStorage.setItem('fcis_projectId', e.target.value);
              }}
              className="select-customer"
              search={
                <SelectOptionSearch
                  label="Search"
                  placeholder="Search"
                  onChange={e => handleFilter(e.target.value)}
                  value={searchString}
                />
              }
            >
              {filteredCustomers.map((customer: { id: string; name: string }) => (
                <SelectOption
                  key={customer.id}
                  label={customer.name}
                  value={customer.id}
                />
              ))}
            </Select>
          </div>
          {selectedTab !== 'api_credentials' && (
            <div className="customer-select">
              <Label className="customer-title">{i18next.t('site')}</Label>
              <Select
                aria-label="select-site"
                value={selectedSite ?? siteList[0]?.id}
                id="select-site-header"
                size="large"
                className="select-customer"
                disabled={loadingSites}
                onChange={e => {
                  setSelectedSite(e.target.value);
                  sessionStorage.setItem('fcis_siteId', e.target.value);
                }}
              >
                {siteList.map(site => (
                  <SelectOption
                    key={site.id}
                    label={site.siteName}
                    value={site.id}
                  />
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>
      <div className="integrationTabsWrapper">
        {!isNewIntegrationEnabled ? (
          <div>
            <div className="integrationTabs">
              <Tabs
                iconPosition="left"
                onChange={value => {
                  setSelectedTab(value);
                  if (value === 'api_credentials') {
                    history.push('/app/feature/integrations/integration');
                  } else {
                    history.push('/app/feature/integrations/workflow');
                  }
                }}
                value={selectedTab}
              >
                <Tab id="workflows">{i18next.t('workflows')}</Tab>
                <Tab id="api_credentials">{i18next.t('apiCredentials')}</Tab>
              </Tabs>
            </div>
            {selectedTab === 'workflows' && (
              <div className="application-Wrapper">
                <Card className="workflowWrapper">
                  <Divider />
                  {loadingSubscription && (
                    <div className="gateway-loader-cls">
                      <ContentLoader />
                    </div>
                  )}
                  {selectedSite &&
                    !loadingSubscription &&
                    !hasWorkflowSubscription && <NoSubscription />}
                  {!loadingSubscription && hasWorkflowSubscription && (
                    <IntegrationsWorflowMicrofrontendPage
                      key={`${selectedCustomer ??
                        'no-customer'}:${selectedSite ?? 'no-site'}`}
                    />
                  )}
                </Card>
              </div>
            )}
            {selectedTab === 'api_credentials' && (
              <div className="application-Wrapper">
                <ClientProviderWrapper>
                  <Suspense fallback={null}>
                    {selectedCustomer ? (
                      <IntegrationMicroFrontend
                        customerId={selectedCustomer}
                        userDetail={selectedUserDetail}
                      />
                    ) : !showError ? (
                      <div className="gateway-loader-cls">
                        <ContentLoader />
                      </div>
                    ) : (
                      <Notification severity="important">
                        <Icon name="badge-alert" size="large" />
                        <span>{i18next.t('accessibleCustomersError')}</span>
                      </Notification>
                    )}
                  </Suspense>
                </ClientProviderWrapper>
              </div>
            )}
          </div>
        ) : enableWorkFlowsForThirdpartyIntegrations ? (
          <div>
            <div className="integrationTabs">
              {showWorkflowsTab ? (
                <Tabs
                  iconPosition="left"
                  onChange={value => {
                    setSelectedTab(value);
                    if (value === 'api_credentials') {
                      history.push('/app/feature/integrations/integration');
                    } else if (value === 'integrations') {
                      history.push('/app/feature/integrations/workflows');
                    } else if (value === 'templates') {
                      history.push('/app/feature/integrations/templates');
                    } else {
                      history.push('/app/feature/integrations/workflow');
                    }
                  }}
                  value={selectedTab}
                >
                  <Tab id="workflows">{i18next.t('workflows')}</Tab>
                  <Tab id="integrations">{i18next.t('integrationsTab')}</Tab>
                  <Tab id="templates">{i18next.t('templates')}</Tab>
                  <Tab id="api_credentials">{i18next.t('apiCredentials')}</Tab>
                </Tabs>
              ) : (
                <Tabs
                  iconPosition="left"
                  onChange={value => {
                    setSelectedTab(value);
                    if (value === 'api_credentials') {
                      history.push('/app/feature/integrations/integration');
                    } else if (value === 'integrations') {
                      history.push('/app/feature/integrations/workflows');
                    } else if (value === 'templates') {
                      history.push('/app/feature/integrations/templates');
                    }
                  }}
                  value={selectedTab}
                >
                  <Tab id="integrations">{i18next.t('integrationsTab')}</Tab>
                  <Tab id="templates">{i18next.t('templates')}</Tab>
                  <Tab id="api_credentials">{i18next.t('apiCredentials')}</Tab>
                </Tabs>
              )}
            </div>
            {selectedTab === 'api_credentials' && (
              <div className="application-Wrapper">
                <ClientProviderWrapper>
                  <Suspense fallback={null}>
                    {selectedCustomer ? (
                      <IntegrationMicroFrontend
                        customerId={selectedCustomer}
                        userDetail={selectedUserDetail}
                      />
                    ) : !showError ? (
                      <div className="gateway-loader-cls">
                        <ContentLoader />
                      </div>
                    ) : (
                      <Notification severity="important">
                        <Icon name="badge-alert" size="large" />
                        <span>{i18next.t('accessibleCustomersError')}</span>
                      </Notification>
                    )}
                  </Suspense>
                </ClientProviderWrapper>
              </div>
            )}
            {selectedTab === 'workflows' && (
              <div className="application-Wrapper">
                <Card className="workflowWrapper">
                  <Divider />
                  {loadingSubscription && (
                    <div className="gateway-loader-cls">
                      <ContentLoader />
                    </div>
                  )}
                  {selectedSite &&
                    !loadingSubscription &&
                    !hasWorkflowSubscription && <NoSubscription />}
                  {!loadingSubscription && hasWorkflowSubscription && (
                    <IntegrationsWorflowMicrofrontendPage
                      key={`${selectedCustomer ??
                        'no-customer'}:${selectedSite ?? 'no-site'}`}
                    />
                  )}
                </Card>
              </div>
            )}
            {selectedTab === 'integrations' && (
              <div className="">
                {loadingSubscription && (
                  <div className="gateway-loader-cls">
                    <ContentLoader />
                  </div>
                )}
                {selectedSite && !loadingSubscription && !hasWorkflowSubscription && <NoSubscription />}
                {!loadingSubscription && hasWorkflowSubscription && (
                  <EasyIntegrateIframe
                    ref={integrationsIframeRef}
                    path="/workflows"
                    iframeId="integrations-iframe-id"
                    orgSelected={easyIntegrateOrg}
                    onEditorStateChange={handleEditorStateChange}
                  />
                )}
              </div>
            )}
            {selectedTab === 'templates' && (
              <div className="">
                {loadingSubscription && (
                  <div className="gateway-loader-cls">
                    <ContentLoader />
                  </div>
                )}
                {selectedSite && !loadingSubscription && !hasWorkflowSubscription && <NoSubscription />}
                {!loadingSubscription && hasWorkflowSubscription && (
                  <EasyIntegrateIframe
                    ref={integrationsIframeRef}
                    path="/templates"
                    iframeId="templates-iframe-id"
                    orgSelected={easyIntegrateOrg}
                    onEditorStateChange={handleEditorStateChange}
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="integrationTabs">
              {showLegacyWorkflow ? (
                <Tabs
                  iconPosition="left"
                  onChange={value => {
                    setSelectedTab(value);
                    if (value === 'api_credentials') {
                      history.push('/app/feature/integrations/integration');
                    } else if (value === 'integrations') {
                      history.push('/app/feature/integrations/workflows');
                    } else if (value === 'templates') {
                      history.push('/app/feature/integrations/templates');
                    } else {
                      history.push('/app/feature/integrations/workflow');
                    }
                  }}
                  value={selectedTab}
                >
                  <Tab id="workflows">{i18next.t('workflows')}</Tab>
                  <Tab id="integrations">{i18next.t('integrationsTab')}</Tab>
                  <Tab id="templates">{i18next.t('templates')}</Tab>
                  <Tab id="api_credentials">{i18next.t('apiCredentials')}</Tab>
                </Tabs>
              ) : (
                <Tabs
                  iconPosition="left"
                  onChange={value => {
                    setSelectedTab(value);
                    if (value === 'api_credentials') {
                      history.push('/app/feature/integrations/integration');
                    } else if (value === 'integrations') {
                      history.push('/app/feature/integrations/workflows');
                    } else if (value === 'templates') {
                      history.push('/app/feature/integrations/templates');
                    }
                  }}
                  value={selectedTab}
                >
                  <Tab id="integrations">{i18next.t('integrationsTab')}</Tab>
                  <Tab id="templates">{i18next.t('templates')}</Tab>
                  <Tab id="api_credentials">{i18next.t('apiCredentials')}</Tab>
                </Tabs>
              )}
            </div>
            {selectedTab === 'api_credentials' && (
              <div className="application-Wrapper">
                <ClientProviderWrapper>
                  <Suspense fallback={null}>
                    {selectedCustomer ? (
                      <IntegrationMicroFrontend
                        customerId={selectedCustomer}
                        userDetail={selectedUserDetail}
                      />
                    ) : !showError ? (
                      <div className="gateway-loader-cls">
                        <ContentLoader />
                      </div>
                    ) : (
                      <Notification severity="important">
                        <Icon name="badge-alert" size="large" />
                        <span>{i18next.t('accessibleCustomersError')}</span>
                      </Notification>
                    )}
                  </Suspense>
                </ClientProviderWrapper>
              </div>
            )}
            {selectedTab === 'workflows' && showLegacyWorkflow && (
              <div className="application-Wrapper">
                <Card className="workflowWrapper">
                  <Divider />
                  {loadingSubscription && (
                    <div className="gateway-loader-cls">
                      <ContentLoader />
                    </div>
                  )}
                  {selectedSite &&
                    !loadingSubscription &&
                    !hasWorkflowSubscription && <NoSubscription />}
                  {!loadingSubscription && hasWorkflowSubscription && (
                    <IntegrationsWorflowMicrofrontendPage
                      key={`${selectedCustomer ??
                        'no-customer'}:${selectedSite ?? 'no-site'}`}
                    />
                  )}
                </Card>
              </div>
            )}
            {selectedTab === 'integrations' && (
              <div className="">
                {loadingSubscription && (
                  <div className="gateway-loader-cls">
                    <ContentLoader />
                  </div>
                )}
                {selectedSite && !loadingSubscription && !hasWorkflowSubscription && <NoSubscription />}
                {!loadingSubscription && hasWorkflowSubscription && (
                  <EasyIntegrateIframe
                    ref={integrationsIframeRef}
                    path="/workflows"
                    iframeId="integrations-iframe-id"
                    orgSelected={easyIntegrateOrg}
                    onEditorStateChange={handleEditorStateChange}
                  />
                )}
              </div>
            )}
            {selectedTab === 'templates' && (
              <div className="">
                {loadingSubscription && (
                  <div className="gateway-loader-cls">
                    <ContentLoader />
                  </div>
                )}
                {selectedSite && !loadingSubscription && !hasWorkflowSubscription && <NoSubscription />}
                {!loadingSubscription && hasWorkflowSubscription && (
                  <EasyIntegrateIframe
                    ref={integrationsIframeRef}
                    path="/templates"
                    iframeId="templates-iframe-id"
                    orgSelected={easyIntegrateOrg}
                    onEditorStateChange={handleEditorStateChange}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationMicroFrontendPage;
